import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiInfo, FiCopy, FiCheck } from 'react-icons/fi';
import vinDecoderService from '../services/vinDecoderService';

const VINDecoder = () => {
  const [vin, setVin] = useState('');
  const [year, setYear] = useState('');
  const [decodedData, setDecodedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Sample VINs for testing
  const sampleVINs = [
    { vin: '1HGCM82633A123456', desc: '2003 Honda Accord' },
    { vin: '5UXWX7C5XBA123456', desc: '2011 BMW X3' },
    { vin: 'WBANE53517CM12345', desc: '2007 BMW 3 Series' }
  ];

  const handleDecode = async () => {
    if (!vin || vin.trim().length === 0) {
      setError('Please enter a VIN');
      return;
    }

    setLoading(true);
    setError(null);
    setDecodedData(null);

    try {
      const result = await vinDecoderService.decodeVIN(vin, year || null);
      
      if (result.error) {
        setError(result.message);
        return;
      }

      // Show warnings if present
      if (result.warnings) {
        setError(`Note: ${result.warnings}`);
      }

      setDecodedData(result.data);
    } catch (err) {
      console.error('Error decoding VIN:', err);
      setError('Failed to decode VIN. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSampleVin = (sampleVin) => {
    setVin(sampleVin);
    setError(null);
  };

  return (
    <>
      
      <div className="bg-gray-50 py-6">
        <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">VIN Decoder Tool</h1>
            <p className="text-lg text-gray-600">
              Enter a Vehicle Identification Number (VIN) to get detailed vehicle information
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Powered by NHTSA (National Highway Traffic Safety Administration) Database
            </p>
          </div>

          {/* Input Section */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  VIN (17 characters)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={vin}
                    onChange={(e) => setVin(e.target.value.toUpperCase())}
                    placeholder="Enter 17-character VIN"
                    maxLength={17}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    onClick={handleDecode}
                    disabled={loading || !vin}
                    className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Decoding...
                      </>
                    ) : (
                      <>
                        <FiSearch />
                        Decode
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model Year (Optional - for better accuracy)
                </label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  placeholder="e.g., 2023"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Sample VINs */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Try a sample VIN:</p>
                <div className="flex flex-wrap gap-2">
                  {sampleVINs.map((sample) => (
                    <button
                      key={sample.vin}
                      onClick={() => handleSampleVin(sample.vin)}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition-colors"
                    >
                      {sample.desc}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700">{error}</p>
              </div>
            )}
          </div>

          {/* Results Section */}
          {decodedData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <div className="bg-primary-600 text-white p-6">
                <h2 className="text-2xl font-bold mb-2">
                  {decodedData.year} {decodedData.make} {decodedData.model}
                </h2>
                {decodedData.trim && (
                  <p className="text-primary-100">{decodedData.trim}</p>
                )}
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem label="VIN" value={decodedData.vin} copyable />
                    <InfoItem label="Year" value={decodedData.year} />
                    <InfoItem label="Make" value={decodedData.make} />
                    <InfoItem label="Model" value={decodedData.model} />
                    <InfoItem label="Trim" value={decodedData.trim} />
                    <InfoItem label="Body Type" value={decodedData.bodyType} />
                    <InfoItem label="Vehicle Type" value={decodedData.vehicleType} />
                    <InfoItem label="Doors" value={decodedData.doors} />
                  </div>
                </div>

                {/* Engine & Performance */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Engine & Performance</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem label="Engine Cylinders" value={decodedData.engineCylinders} />
                    <InfoItem label="Engine Size" value={decodedData.engineLiters ? `${decodedData.engineLiters}L` : null} />
                    <InfoItem label="Horsepower" value={decodedData.engineHP ? `${decodedData.engineHP} HP` : null} />
                    <InfoItem label="Engine Model" value={decodedData.engineModel} />
                    <InfoItem label="Engine Configuration" value={decodedData.engineConfiguration} />
                    <InfoItem label="Turbo" value={decodedData.turbo ? 'Yes' : 'No'} />
                    <InfoItem label="Fuel Type" value={decodedData.fuelType} />
                    <InfoItem label="Transmission" value={decodedData.transmission} />
                    <InfoItem label="Drivetrain" value={decodedData.drivetrain} />
                  </div>
                </div>

                {/* Manufacturing Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Manufacturing Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem label="Manufacturer" value={decodedData.manufacturerName} />
                    <InfoItem label="Plant City" value={decodedData.plantCity} />
                    <InfoItem label="Plant State" value={decodedData.plantState} />
                    <InfoItem label="Plant Country" value={decodedData.plantCountry} />
                  </div>
                </div>

                {/* Safety Features */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Safety Features</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem label="ABS" value={decodedData.abs ? 'Yes' : 'No'} />
                    <InfoItem label="ESC" value={decodedData.esc ? 'Yes' : 'No'} />
                    <InfoItem label="Traction Control" value={decodedData.tractionControl ? 'Yes' : 'No'} />
                    <InfoItem label="Seats" value={decodedData.seats} />
                  </div>
                </div>

                {/* Additional Details */}
                {decodedData.description && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                    <p className="text-gray-700">{decodedData.description}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Info Box */}
          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <FiInfo className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-2">About VIN Decoding</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>A VIN is a unique 17-character identifier for every vehicle</li>
                  <li>VIN decoding reveals detailed specifications and manufacturing information</li>
                  <li>Data is sourced from NHTSA's official database</li>
                  <li>Some fields may be empty depending on manufacturer reporting</li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
    </>
  );
};

// Info Item Component
const InfoItem = ({ label, value, copyable = false }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!value || value === 'null' || value === 'undefined') return null;

  return (
    <div className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
      <div className="flex-1">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value}</p>
      </div>
      {copyable && value && (
        <button
          onClick={handleCopy}
          className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          title="Copy to clipboard"
        >
          {copied ? <FiCheck className="text-green-500" /> : <FiCopy />}
        </button>
      )}
    </div>
  );
};

export default VINDecoder;
